import { useAuth0 } from "@auth0/auth0-react";
import AddressCard from "common/ui/AddressCard";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchUserInfo, updateStripeCustomerId } from "store/slices/userSlice";

import api from "api/api";
import endpoints from "api/endpoints";
import ToastNotification from "common/toast/Toast";
import { formatCurrency, hasNetTerms, paymentTermsLabel } from "config/config";
import { normalizeStateInput } from "config/states";

import StripeWrapper from "../stripe/StripeWrapper";

import AddPaymentMethod from "./AddPaymentMethod";
import AddressForm from "./AddressForm";
import CheckoutSectionCard from "./CheckoutSectionCard";
import SavedPaymentMethods from "./SavedPaymentMethods";

import { SFC_FREE_SHIPPING, SHIPPING_DEFAULT } from "@/constants/constant";
import { Button, Checkbox, Icon } from "@/design-system";

// The reference's payment-method tab: a bordered two-line button whose selection
// is the same 1.5px orange border + neutral-50 fill as the delivery rows.
const payTabClass = (active) =>
  `flex flex-1 cursor-pointer flex-col items-start gap-[2px] rounded-md border-[1.5px] bg-transparent px-[var(--space-4)] py-[var(--space-3)] text-left font-sans [transition:border-color_var(--duration-fast),background_var(--duration-fast)] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)] ${
    active
      ? "border-orange-500 bg-[var(--neutral-50)]"
      : "border-line hover:border-line-strong"
  }`;

// The reference's "No delivery address yet" band (grep `showShipEmpty` in
// `Dens N Dente Tailwind.dc.html`): dashed border, amber `wrong_location`, and
// the add action inline — shown instead of the tile grid when the book is empty.
function AddressEmptyState({ kind, onAdd }) {
  return (
    <div className="flex items-center gap-[14px] rounded-md border border-dashed border-line bg-[var(--neutral-50)] px-[var(--space-4)] py-[16px]">
      <Icon
        name="wrong_location"
        size={24}
        className="shrink-0 text-[var(--warning-600)]"
      />
      <div className="flex-1">
        <div className="text-sm font-semibold">No {kind} address yet</div>
        <div className="text-sm text-ink-2">
          Add one now — we&rsquo;ll remember it for your next order.
        </div>
      </div>
      <Button
        size="sm"
        variant="outline"
        startIcon="add_location_alt"
        onClick={onAdd}
      >
        Add address
      </Button>
    </div>
  );
}

export default function CheckoutPayment({
  shippingAddresses,
  setShippingAddresses,
  selectedShippingId,
  setSelectedShippingId,
  billingAddresses,
  setBillingAddresses,
  selectedBillingId,
  setSelectedBillingId,
  onCustomerIdChange,
  orderNote,
  setOrderNote,
}) {
  const userInfo = useSelector((state) => state.user.info);
  const stripeCustomerId = userInfo?.stripeCustomerId;
  const { user, getAccessTokenSilently } = useAuth0();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Payment method states
  const [showAddPaymentMethod, setShowAddPaymentMethod] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("invoice");
  const [shippingMethod, setShippingMethod] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  // Which book has its inline add-address form open — the reference's
  // `coShipFormOpen`, generalised to both books. null | "shipping" | "billing".
  const [addingAddressFor, setAddingAddressFor] = useState(null);
  // The reference's "Billing address is the same as the delivery address"
  // checkbox (`coBillSame`), adapted to the app's billing address book:
  // `null` = the user hasn't touched it, in which case it is ON when the
  // account has no billing address on file (the pasted empty state) and OFF
  // when it has one, so a saved NetSuite billing address keeps being used.
  // Only an explicit tick/untick is persisted (in the checkbox's onChange):
  // persisting the derived default stored "true" on the first render, before
  // the address book had loaded, and every later visit then read it back as a
  // user choice (Oct 2026 review). CheckoutReview applies the same default
  // when the key is absent. sessionStorage, not localStorage: it is this
  // checkout's choice, and a browser-global key outlived abandoned checkouts
  // and leaked to the next account on a shared PC.
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(() => {
    const stored = sessionStorage.getItem("billingSameAsShipping");
    return stored === null ? null : stored === "true";
  });
  const sameAsShipping =
    billingSameAsShipping === null
      ? billingAddresses.length === 0
      : billingSameAsShipping;
  const selectedShippingAddress = shippingAddresses.find(
    (addr) => addr.id === selectedShippingId
  );
  // Inline validation surfaced the reference's way (its `coErrors`): set by a
  // failed Continue, shown beside the button, cleared the moment the user fixes
  // the thing (selects an address / a payment method). Also drives the amber
  // section badges and the scroll-to-error anchors.
  const [stepErrors, setStepErrors] = useState({ ship: "", pay: "" });

  // On-account (invoice) payment is for customers whose NetSuite record
  // carries real net payment terms (`hasNetTerms`: Net 10/15/30/…). Anyone
  // else — no terms, Credit Card, Prepaid, Due On Receipt — pays by card: the
  // On account tab is not rendered at all, Continue re-checks, and the
  // backend refuses the order anyway (`NO_PAYMENT_TERMS`). This replaced the
  // Sep 2026 `searchstage === "Customer"` rule on Oct 21 2026.
  const canPayOnAccount = hasNetTerms(userInfo);
  const termsLabel = paymentTermsLabel(userInfo);

  useEffect(() => {
    if (!canPayOnAccount) {
      setPaymentMethod("card");
    }
  }, [canPayOnAccount]);

  // Notify parent component when stripeCustomerId changes
  useEffect(() => {
    if (onCustomerIdChange) {
      onCustomerIdChange(stripeCustomerId);
    }
  }, [stripeCustomerId, onCustomerIdChange]);

  // Save addresses and selectedIds to localStorage whenever they change
  useEffect(() => {
    if (shippingAddresses.length > 0 || billingAddresses.length > 0) {
      localStorage.setItem(
        "checkoutShippingAddresses",
        JSON.stringify(shippingAddresses)
      );
      localStorage.setItem(
        "checkoutBillingAddresses",
        JSON.stringify(billingAddresses)
      );
    }
    if (selectedShippingId) {
      localStorage.setItem(
        "selectedShippingAddressId",
        selectedShippingId.toString()
      );
    }
    if (selectedBillingId) {
      localStorage.setItem(
        "selectedBillingAddressId",
        selectedBillingId.toString()
      );
    }
  }, [
    shippingAddresses,
    billingAddresses,
    selectedShippingId,
    selectedBillingId,
  ]);

  // Fetch shipping method based on user category
  useEffect(() => {
    const fetchShippingMethod = async () => {
      setShippingLoading(true);
      try {
        const shippingMethodId =
          userInfo?.category === 5 || userInfo?.category === "5"
            ? SFC_FREE_SHIPPING
            : SHIPPING_DEFAULT;

        const response = await api.get(
          endpoints.GET_SHIPPING_METHOD(shippingMethodId)
        );
        if (response.data && response.data.length > 0) {
          setShippingMethod(response.data[0]);
        }
      } catch (error) {
        // Error fetching shipping method - silently handled
      } finally {
        setShippingLoading(false);
      }
    };

    fetchShippingMethod();
  }, [userInfo?.category]);

  // Selection ≠ default. Selecting picks the address for THIS order only;
  // `isDefault` is the sticky future-orders flag, moved only by the tiles'
  // explicit "Set as default" action below (the reference's address-book
  // behaviour). This used to conflate the two by rewriting isDefault on every
  // select.
  const handleSelectAddress = (id) => {
    setSelectedShippingId(id);
    setStepErrors((prev) => (prev.ship ? { ...prev, ship: "" } : prev));
  };

  const handleRemoveAddress = (id) => {
    setShippingAddresses((prev) => prev.filter((addr) => addr.id !== id));
    if (selectedShippingId === id && shippingAddresses.length > 1) {
      setSelectedShippingId(shippingAddresses[0].id);
    }
  };

  const handleSelectBillingAddress = (id) => {
    setSelectedBillingId(id);
  };

  const handleSetDefaultShipping = (id) => {
    setShippingAddresses((prev) =>
      prev.map((addr) => ({ ...addr, isDefault: addr.id === id }))
    );
    const name = shippingAddresses.find((addr) => addr.id === id)?.fullName;
    ToastNotification.success(
      `Default address updated — new orders will ship to ${name || "this address"}.`
    );
  };

  const handleSetDefaultBilling = (id) => {
    setBillingAddresses((prev) =>
      prev.map((addr) => ({ ...addr, isDefault: addr.id === id }))
    );
    const name = billingAddresses.find((addr) => addr.id === id)?.fullName;
    ToastNotification.success(
      `Default billing address updated — new orders will bill to ${name || "this address"}.`
    );
  };

  const handleRemoveBillingAddress = (id) => {
    setBillingAddresses((prev) => prev.filter((addr) => addr.id !== id));
    if (selectedBillingId === id && billingAddresses.length > 1) {
      setSelectedBillingId(billingAddresses[0].id);
    }
  };

  // The profile modal's proven NetSuite write (`CreateAddressModal.jsx`): PATCH
  // the customer's addressBook with one new line — no line id means append.
  // `defaultShipping`/`defaultBilling` are NetSuite's own flags, so a
  // make-default save survives the next profile seed. Returns false on any
  // failure: the local list is already usable for THIS order, so a failed
  // remote save degrades to a warning, never a blocked save.
  const persistAddressToNetSuite = async (
    addressFields,
    makeDefault,
    addressType
  ) => {
    if (!userInfo?.id) return false;
    try {
      const token = await getAccessTokenSilently();
      await api.patch(
        endpoints.PATCH_UPDATE_CUSTOMER(userInfo.id),
        {
          addressBook: {
            items: [
              {
                addressBookAddress: {
                  addr1: addressFields.address,
                  ...(addressFields.address2
                    ? { addr2: addressFields.address2 }
                    : {}),
                  addressee: addressFields.fullName || "",
                  city: addressFields.city,
                  state: normalizeStateInput("ca", addressFields.state),
                  zip: addressFields.zip,
                  country: { id: "CA" },
                },
                defaultShipping: addressType === "shipping" && makeDefault,
                defaultBilling: addressType === "billing" && makeDefault,
              },
            ],
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh the profile in Redux so a later checkout mount seeds with the
      // new address. useInitialAddress is guarded by isInitialized, so the
      // books on screen don't re-seed mid-session.
      dispatch(fetchUserInfo({ user, getAccessTokenSilently }));
      return true;
    } catch (error) {
      // Surface the real reason in dev — NetSuite's message is in response.data.
      // eslint-disable-next-line no-console
      console.error(
        "Address save to NetSuite failed:",
        error?.response?.data || error
      );
      return false;
    }
  };

  const handleSaveAddress = async (newAddress, addressType) => {
    const id = Date.now();
    // `makeDefault` is the modal's "Set as my default address" checkbox — a flag
    // for THIS function, not an address field; strip it so it never reaches the
    // order payload.
    const { makeDefault, ...addressFields } = newAddress;
    // Normalize full name once so it can be used for both fields and addrText
    const name = addressFields.fullName ?? addressFields.fullname ?? "";
    // Build addrText for review/order payload: name on first line, then address lines
    const addrText = [name, addressFields.address, addressFields.address2]
      .filter(Boolean)
      .join("\n");
    const formatted = {
      ...addressFields,
      id,
      isDefault: Boolean(makeDefault),
      // Normalize for CheckoutReview and order payload (expects fullname + addrText)
      fullname: name,
      fullName: name,
      addrText: addrText || undefined,
    };

    const typeToUse = addressType;

    if (typeToUse === "shipping") {
      const nextShippingAddresses = makeDefault
        ? shippingAddresses
            .map((addr) => ({ ...addr, isDefault: false }))
            .concat(formatted)
        : [...shippingAddresses, formatted];
      setShippingAddresses(nextShippingAddresses);
      // Persist immediately so it's available even if user continues before effect runs
      localStorage.setItem(
        "checkoutShippingAddresses",
        JSON.stringify(nextShippingAddresses)
      );
      localStorage.setItem("selectedShippingAddressId", id.toString());
      setSelectedShippingId(id);
      setStepErrors((prev) => (prev.ship ? { ...prev, ship: "" } : prev));
    } else {
      const nextBillingAddresses = makeDefault
        ? billingAddresses
            .map((addr) => ({ ...addr, isDefault: false }))
            .concat(formatted)
        : [...billingAddresses, formatted];
      setBillingAddresses(nextBillingAddresses);
      // Persist immediately so new billing address is available on review step
      localStorage.setItem(
        "checkoutBillingAddresses",
        JSON.stringify(nextBillingAddresses)
      );
      localStorage.setItem("selectedBillingAddressId", id.toString());
      setSelectedBillingId(id);
    }

    setAddingAddressFor(null);

    // Only claim "we'll remember it" once NetSuite actually has it.
    const savedToAccount = await persistAddressToNetSuite(
      addressFields,
      Boolean(makeDefault),
      typeToUse
    );
    if (savedToAccount) {
      ToastNotification.success(
        makeDefault
          ? "Address saved and set as your default."
          : "Address saved — we'll remember it for next time."
      );
    } else {
      ToastNotification.warning(
        "Address saved for this order, but we couldn't add it to your account — it may not be here next time."
      );
    }
  };

  const handlePaymentMethodAdded = (paymentMethod) => {
    setShowAddPaymentMethod(false);
    setSelectedPaymentMethodId(paymentMethod.id);
    setStepErrors((prev) => (prev.pay ? { ...prev, pay: "" } : prev));
  };

  const handleSelectPaymentMethod = (paymentMethodId) => {
    setSelectedPaymentMethodId(paymentMethodId);
    setStepErrors((prev) => (prev.pay ? { ...prev, pay: "" } : prev));
  };

  const handleCreateStripeCustomer = async () => {
    // The Stripe customer is created under the login email (custentity1) so
    // ownership checks and the login-time lookup both resolve it.
    if (!userInfo?.custentity1) {
      ToastNotification.error(
        "Please ensure your profile has email and name information."
      );
      return;
    }

    setCustomerLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await api.post(
        endpoints.POST_CREATE_STRIPE_CUSTOMER(),
        {
          email: userInfo.custentity1,
          // Person name when NetSuite has one, else the clinic. A bare
          // `${firstname} ${lastname}` produced "undefined undefined" for
          // company customers; the backend now derives the name from the
          // NetSuite record anyway, this is only the fallback it keeps.
          name:
            [userInfo.firstname, userInfo.lastname].filter(Boolean).join(" ") ||
            userInfo.companyname ||
            userInfo.entityid,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.customerId) {
        dispatch(updateStripeCustomerId(response.data.customerId));
        setStepErrors((prev) => (prev.pay ? { ...prev, pay: "" } : prev));
      }
    } catch (error) {
      ToastNotification.error(
        "Failed to create payment account. Please try again."
      );
    } finally {
      setCustomerLoading(false);
    }
  };

  return (
    <StripeWrapper>
      <div className="flex flex-col gap-[var(--space-5)]">
        {/* Shipping Addresses. The amber badge pre-warns an empty book — the
            reference flags "no address on file" before any submit attempt. */}
        <CheckoutSectionCard
          n={1}
          title="Ship to"
          tone={
            shippingAddresses.length === 0 || stepErrors.ship
              ? "warning"
              : undefined
          }
          data-co-ship="1"
        >
          <div className="flex flex-col gap-[var(--space-4)]">
            {shippingAddresses.length > 0 && (
              <div
                role="radiogroup"
                aria-label="Delivery address"
                className="flex flex-col gap-[var(--space-3)]"
              >
                {shippingAddresses.map((addr) => (
                  <AddressCard
                    key={addr.id}
                    address={addr}
                    isSelected={addr.id === selectedShippingId}
                    isDefault={Boolean(addr.isDefault)}
                    onSelect={() => handleSelectAddress(addr.id)}
                    onRemove={() => handleRemoveAddress(addr.id)}
                    onSetDefault={() => handleSetDefaultShipping(addr.id)}
                  />
                ))}

                {addingAddressFor !== "shipping" && (
                  <div className="self-start">
                    <Button
                      size="sm"
                      variant="ghost"
                      startIcon="add"
                      onClick={() => setAddingAddressFor("shipping")}
                    >
                      Add another address
                    </Button>
                  </div>
                )}
              </div>
            )}

            {shippingAddresses.length === 0 &&
              addingAddressFor !== "shipping" && (
                <AddressEmptyState
                  kind="delivery"
                  onAdd={() => setAddingAddressFor("shipping")}
                />
              )}

            {addingAddressFor === "shipping" && (
              <AddressForm
                addressType="shipping"
                onSave={handleSaveAddress}
                onCancel={() => setAddingAddressFor(null)}
              />
            )}
          </div>
        </CheckoutSectionCard>

        {/* Billing Addresses */}
        <CheckoutSectionCard n={2} title="Bill to">
          <div className="flex flex-col gap-[var(--space-4)]">
            {/* Reference checkout: "Billing address is the same as the delivery
                address" checkbox; checked hides the billing book and the review
                step bills to the selected delivery address. */}
            <Checkbox
              shape="round"
              checked={sameAsShipping}
              onChange={(checked) => {
                setBillingSameAsShipping(checked);
                sessionStorage.setItem(
                  "billingSameAsShipping",
                  String(checked)
                );
                // Close only the billing book's inline form — the flag is
                // shared with Ship to, whose half-typed address must survive.
                if (checked && addingAddressFor === "billing") {
                  setAddingAddressFor(null);
                }
              }}
              label="Billing address is the same as the delivery address"
              description={
                sameAsShipping
                  ? selectedShippingAddress
                    ? `Billing to ${selectedShippingAddress.fullName}, ${[
                        selectedShippingAddress.city,
                        selectedShippingAddress.state,
                        selectedShippingAddress.zip,
                      ]
                        .filter(Boolean)
                        .join(" ")}`
                    : "Choose a delivery address in step 1 and we'll bill there too."
                  : undefined
              }
            />

            {!sameAsShipping && billingAddresses.length > 0 && (
              <div
                role="radiogroup"
                aria-label="Billing address"
                className="flex flex-col gap-[var(--space-3)]"
              >
                {billingAddresses.map((addr) => (
                  <AddressCard
                    key={addr.id}
                    address={addr}
                    isSelected={addr.id === selectedBillingId}
                    isDefault={Boolean(addr.isDefault)}
                    onSelect={() => handleSelectBillingAddress(addr.id)}
                    onRemove={() => handleRemoveBillingAddress(addr.id)}
                    onSetDefault={() => handleSetDefaultBilling(addr.id)}
                  />
                ))}

                {addingAddressFor !== "billing" && (
                  <div className="self-start">
                    <Button
                      size="sm"
                      variant="ghost"
                      startIcon="add"
                      onClick={() => setAddingAddressFor("billing")}
                    >
                      Add another address
                    </Button>
                  </div>
                )}
              </div>
            )}

            {!sameAsShipping &&
              billingAddresses.length === 0 &&
              addingAddressFor !== "billing" && (
                <AddressEmptyState
                  kind="billing"
                  onAdd={() => setAddingAddressFor("billing")}
                />
              )}

            {!sameAsShipping && addingAddressFor === "billing" && (
              <AddressForm
                addressType="billing"
                onSave={handleSaveAddress}
                onCancel={() => setAddingAddressFor(null)}
              />
            )}
          </div>
        </CheckoutSectionCard>

        {/* Payment Method Selection */}
        <CheckoutSectionCard
          n={3}
          title="Payment"
          tone={stepErrors.pay ? "warning" : undefined}
          data-co-pay="1"
        >
          {/* The reference's payment step: the two-tab chooser (On account /
              Credit card) renders only for `onAccountEligible` customers; a
              `newAccount` sees no tabs at all, just the blue info band, and pays
              by card. Copy follows the reference minus its PO mentions — the
              Aug 5 "No PO numbers" rule (BEHAVIOR.md) postdates that markup. */}
          {canPayOnAccount ? (
            <div className="mb-[18px] flex gap-[var(--space-3)]">
              <button
                type="button"
                className={payTabClass(paymentMethod === "invoice")}
                onClick={() => {
                  setPaymentMethod("invoice");
                  setStepErrors((prev) =>
                    prev.pay ? { ...prev, pay: "" } : prev
                  );
                }}
              >
                <b className="text-sm">On account</b>
                <small className="text-xs text-ink-2">{termsLabel} terms</small>
              </button>
              <button
                type="button"
                className={payTabClass(paymentMethod === "card")}
                onClick={() => {
                  setPaymentMethod("card");
                  setStepErrors((prev) =>
                    prev.pay ? { ...prev, pay: "" } : prev
                  );
                }}
              >
                <b className="text-sm">Credit card</b>
                <small className="text-xs text-ink-2">Pay now</small>
              </button>
            </div>
          ) : (
            <div className="mb-[18px] flex items-start gap-[var(--space-3)] rounded-md border border-blue-100 bg-soft px-[var(--space-4)] py-[14px]">
              <Icon
                name="info"
                size={20}
                fill
                className="shrink-0 text-blue-600"
              />
              <div className="text-sm text-blue-900">
                Pay by card for now. Once your rep sets up your account terms,
                you'll be able to pay on account — usually within 1 business
                day.
              </div>
            </div>
          )}

          {/* On-account confirmation band (reference: "Net 30 terms. We'll
              invoice {clinic} — nothing due today.") */}
          {paymentMethod === "invoice" && canPayOnAccount && (
            <div className="flex items-center gap-[var(--space-3)] rounded-md bg-success-50 px-[var(--space-4)] py-[14px]">
              <Icon
                name="verified"
                size={22}
                fill
                className="shrink-0 text-success-600"
              />
              <div className="text-sm text-success-700">
                <b>{termsLabel} terms.</b> We'll invoice{" "}
                {userInfo?.companyname || "your practice"} — nothing due today.
              </div>
            </div>
          )}

          {/* Credit/Debit Card Content - Show Payment Methods Section */}
          {paymentMethod === "card" && (
            <div className="flex flex-col gap-[var(--space-4)]">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold">Saved payment methods</h4>
                {!showAddPaymentMethod && stripeCustomerId && (
                  <Button
                    variant="outline"
                    size="sm"
                    startIcon="add_card"
                    onClick={() => setShowAddPaymentMethod(true)}
                    disabled={customerLoading}
                  >
                    Add payment method
                  </Button>
                )}
              </div>

              {customerLoading ? (
                <div className="flex flex-col gap-[var(--space-3)] rounded-md border border-line-subtle bg-[var(--neutral-50)] p-[var(--space-5)]">
                  <div className="dnd-skel h-[14px] w-1/4 rounded-pill" />
                  <div className="dnd-skel h-[64px] rounded-md" />
                  <div className="dnd-skel h-[64px] rounded-md" />
                </div>
              ) : !stripeCustomerId ? (
                <div className="rounded-md border border-line-subtle bg-[var(--neutral-50)] p-[var(--space-5)]">
                  <div className="text-center text-ink-2">
                    <div className="mb-[var(--space-1)] text-md font-semibold text-ink">
                      No payment account yet
                    </div>
                    <div className="mb-[var(--space-4)] text-sm">
                      You need a payment account to save and manage cards.
                    </div>
                    <Button
                      onClick={handleCreateStripeCustomer}
                      disabled={customerLoading}
                    >
                      {customerLoading
                        ? "Creating..."
                        : "Create Payment Account"}
                    </Button>
                  </div>
                </div>
              ) : showAddPaymentMethod ? (
                <AddPaymentMethod
                  customerId={stripeCustomerId}
                  onPaymentMethodAdded={handlePaymentMethodAdded}
                  onCancel={() => setShowAddPaymentMethod(false)}
                />
              ) : (
                <SavedPaymentMethods
                  customerId={stripeCustomerId}
                  onSelectPaymentMethod={handleSelectPaymentMethod}
                  selectedPaymentMethodId={selectedPaymentMethodId}
                  showTitle={false}
                />
              )}
            </div>
          )}
        </CheckoutSectionCard>

        {/* Delivery Method */}
        <CheckoutSectionCard n={4} title="Delivery">
          {shippingLoading ? (
            <div className="dnd-skel h-[64px] rounded-md" />
          ) : (
            // The single delivery option, in the reference's chosen-row dress:
            // 1.5px orange border, filled truck, price on the right.
            <div className="flex items-center gap-[14px] rounded-md border-[1.5px] border-orange-500 bg-[var(--neutral-50)] px-[18px] py-[var(--space-4)]">
              <Icon
                name="local_shipping"
                size={22}
                fill
                className="shrink-0 text-orange-500"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold">
                  {shippingMethod?.itemid || "Purolator Ground"}
                </div>
              </div>
              <b
                className={`text-sm ${
                  Number(shippingMethod?.shippingflatrateamount || 0) === 0
                    ? "text-success-600"
                    : ""
                }`}
              >
                {Number(shippingMethod?.shippingflatrateamount || 0) === 0
                  ? "Free"
                  : formatCurrency(shippingMethod?.shippingflatrateamount)}
              </b>
            </div>
          )}

          <div className="mt-[var(--space-4)]">
            <label className="mb-[var(--space-1)] block text-sm font-medium text-ink-2">
              Shipping note (optional)
            </label>
            <textarea
              value={orderNote}
              onChange={(e) => setOrderNote(e.target.value)}
              rows={3}
              placeholder="Add any special instructions or comments for this order…"
              className="w-full rounded-sm border-[1.5px] border-line bg-card px-[12px] py-[8px] font-sans text-sm text-ink outline-none placeholder:text-ink-3 [transition:border-color_var(--duration-fast)] focus:border-line-strong focus-visible:[box-shadow:var(--focus-ring)]"
            />
            <div className="mt-[var(--space-1)] text-xs text-ink-3">
              This note will be shown on the next step before placing your
              order.
            </div>
          </div>
        </CheckoutSectionCard>

        {/* Continue to Review. Validation lands inline the reference's way
            (its `placeOrder`): amber badge on the offending section, a summary
            beside the action, and a smooth scroll to the section's data-co
            anchor — not a toast the user has to catch before it fades. */}
        <div className="flex flex-wrap items-center justify-end gap-[var(--space-4)]">
          {(stepErrors.ship || stepErrors.pay) && (
            <div className="flex items-start gap-[8px] text-sm text-error-600">
              <Icon name="error" size={18} fill className="mt-[1px] shrink-0" />
              <span>
                {stepErrors.ship && stepErrors.pay
                  ? "Almost there — check Ship to (step 1) and Payment (step 3)."
                  : stepErrors.ship || stepErrors.pay}
              </span>
            </div>
          )}
          <Button
            endIcon="chevron_right"
            onClick={() => {
              // One pass over both sections so the badges light up together.
              const errs = { ship: "", pay: "" };
              if (!selectedShippingId) {
                errs.ship = "Choose a delivery address in step 1.";
              }
              if (paymentMethod === "invoice" && !canPayOnAccount) {
                errs.pay =
                  "On-account payment needs payment terms on your account — pay by card, or contact sales to set up terms.";
              } else if (paymentMethod === "card") {
                if (!stripeCustomerId) {
                  errs.pay = "Create a payment account first.";
                } else if (!selectedPaymentMethodId) {
                  errs.pay = "Select or add a payment method in step 3.";
                }
              }

              if (errs.ship || errs.pay) {
                setStepErrors(errs);
                const anchor = document.querySelector(
                  errs.ship ? "[data-co-ship]" : "[data-co-pay]"
                );
                if (anchor) {
                  window.scrollTo({
                    top:
                      anchor.getBoundingClientRect().top + window.scrollY - 180,
                    behavior: "smooth",
                  });
                }
                return;
              }
              setStepErrors({ ship: "", pay: "" });

              // Save selected payment method and payment type to localStorage for the next step
              localStorage.setItem(
                "selectedPaymentMethodId",
                selectedPaymentMethodId || ""
              );
              localStorage.setItem("paymentMethod", paymentMethod);

              navigate("/checkout/review");
            }}
          >
            Continue to Review
          </Button>
        </div>
      </div>
    </StripeWrapper>
  );
}
